import { useEffect, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { can, useAuth } from '../lib/auth';
import { inr, rupees, unitLabel, type PastingUnit } from '../lib/pasting';

/**
 * Saved monthly contractor bills — api/src/routes/billing.ts.
 *
 * Generate saves the month's live bill as one DRAFT per contractor; a draft
 * is a snapshot that Regenerate refreshes from the entries.  Finalize makes
 * the draft the payment record and locks every production entry on it: from
 * then on an entry changes only through the audited correction on the
 * Entries tab, and money moves only through reasoned adjustments.  Unlock
 * (Super Admin) turns a finalized bill back into a draft.
 */

interface BillLineDto {
  id?: number;
  operation: string;
  /** How these entries differ from the whole job: "without Sorting"… ('' = whole job). */
  variant: string;
  unit: PastingUnit;
  countsAsProduced: boolean;
  qty: number;
  hours: number;
  rate: number | null;
  amount: number | null;
  entries: number;
}

interface AdjustmentDto {
  id: number;
  amount: number;
  reason: string;
  createdOn: string;
  createdBy: string | null;
}

interface BillDto {
  id: number;
  month: string;
  contractorKey: string;
  contractor: string;
  status: 'DRAFT' | 'FINALIZED';
  producedCartons: number;
  workTotal: number;
  adjustmentTotal: number;
  grandTotal: number;
  missingRates: string[];
  generatedOn: string;
  generatedBy: string | null;
  finalizedOn: string | null;
  finalizedBy: string | null;
  unlockedOn: string | null;
  unlockedBy: string | null;
  unlockReason: string | null;
  entryCount: number;
  lines: BillLineDto[];
  adjustments: AdjustmentDto[];
  /** DRAFT whose entries moved since it was generated — regenerate before finalizing. */
  stale: boolean;
  liveTotal: number;
  /** FINALIZED whose entries were corrected since: live work total − saved work total. */
  drift: number;
}

interface BillEntryDto {
  id: number;
  date: string;
  jobId: string;
  jobName: string;
  operation: string;
  unit: PastingUnit;
  qty: number;
  hours: number;
  rate: number | null;
  amount: number | null;
  locked: boolean;
  /** Waiting hours: why the contractor waited. */
  remarks?: string;
}

interface BillDetail extends BillDto {
  entries: BillEntryDto[];
}

interface UnbilledDto {
  key: string;
  contractor: string;
  producedCartons: number;
  lines: BillLineDto[];
  total: number;
  missingRates: string[];
  entryCount: number;
}

interface MonthResponse {
  month: string;
  bills: BillDto[];
  unbilled: UnbilledDto[];
  liveGrandTotal: number;
  /** Entries of the month not verified yet — on no bill until they are. */
  notVerified?: number;
}

interface GenerateResult {
  created: number;
  refreshed: number;
  removed: number;
  keptForAdjustments: number;
  skippedFinalized: string[];
  notVerified?: number;
}

/** The API's error codes in words. */
export function friendlyBillError(e: unknown): string {
  const msg = e instanceof Error ? e.message : String(e);
  if (msg === 'billing_migration_pending') return 'The server database needs its update (contractor billing) first.';
  if (msg === 'bill_stale') return 'Entries changed since this draft was generated. Regenerate the month, check the bill, then finalize.';
  if (msg === 'bill_empty') return 'This bill has no entries and no adjustments — nothing to finalize.';
  if (msg === 'bill_already_finalized') return 'This bill is already finalized — reload the page.';
  if (msg === 'bill_not_finalized') return 'This bill is not finalized — reload the page.';
  if (msg === 'bill_not_found') return 'Bill not found — it may have been removed by a regenerate. Reload the page.';
  if (msg === 'adjustment_not_found') return 'That adjustment is already gone — reload the page.';
  if (msg === 'bill_finalized_use_counter_adjustment') {
    return 'A finalized bill keeps its adjustments. Add an opposite adjustment instead.';
  }
  if (msg === 'invalid_month') return 'Pick a month.';
  if (msg === 'validation_failed') return 'Check the amount (at least ₹0.01) and the reason (at least 3 letters).';
  if (msg === 'forbidden') return 'You do not have permission for this.';
  const mr = /^bill_missing_rates:(.*)$/.exec(msg);
  if (mr) return `No rate set for: ${mr[1]?.split(' | ').join(', ')}. Set them on Pasting Rates, regenerate, then finalize.`;
  const fe = /^entries_on_finalized_bill:(.*)$/.exec(msg);
  if (fe) return `Entries ${fe[1]} are already on a finalized bill — the data was changed outside the app. Ask Super Admin.`;
  return msg;
}

const thisMonth = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }).slice(0, 7);

const monthTitle = (ym: string) =>
  new Date(`${ym}-15T00:00:00Z`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });

const dateText = (iso: string) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', timeZone: 'UTC' });

const when = (iso: string | null) =>
  (iso
    ? new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '');

const qtyCell = (unit: PastingUnit, qty: number, hours: number) =>
  (unit === 'PER_HOUR' ? `${inr(hours, 2)} h` : inr(qty));

const workText = (x: { operation: string; variant?: string }) =>
  (x.variant ? `${x.operation} — ${x.variant}` : x.operation);

/** An entry's work, with the reason for waiting hours. */
const entryWork = (e: BillEntryDto) =>
  (e.unit === 'PER_HOUR' && e.remarks ? `${e.operation} — ${e.remarks}` : e.operation);

/** +₹1,200.00 / −₹350.00 */
const signedRupees = (n: number) => `${n < 0 ? '−' : '+'}${rupees(Math.abs(n))}`;

export function ContractorBills({ legacy }: { legacy?: ReactNode }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  // VIEW_BILLING looks; MANAGE_BILLING generates, finalizes and adjusts.
  const canManage = can(user, 'MANAGE_BILLING');
  // Month (the bills) or any dates (each contractor's work between them).
  const [mode, setMode] = useState<'month' | 'range'>('month');
  const [rangeFrom, setRangeFrom] = useState(() => `${thisMonth()}-01`);
  const [rangeTo, setRangeTo] = useState(() => new Date(Date.now() + 5.5 * 3600_000).toISOString().slice(0, 10));
  const [month, setMonth] = useState(thisMonth);
  const [only, setOnly] = useState('');
  const [rangeNames, setRangeNames] = useState<{ key: string; name: string }[]>([]);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const validMonth = /^\d{4}-(0[1-9]|1[0-2])$/.test(month);

  const data = useQuery({
    queryKey: ['billing', month],
    queryFn: () => api<MonthResponse>(`/api/billing?month=${month}`),
    enabled: validMonth && mode === 'month',
    // An answer from the API (403, 503 migration pending…) will not change on retry.
    retry: (n, e) => n < 2 && !(e instanceof ApiError),
  });

  const generate = useMutation({
    mutationFn: () => api<GenerateResult>('/api/billing/generate', { method: 'POST', body: JSON.stringify({ month }) }),
    onSuccess: (r) => {
      const parts = [
        r.created && `${r.created} created`,
        r.refreshed && `${r.refreshed} refreshed`,
        r.removed && `${r.removed} empty draft${r.removed === 1 ? '' : 's'} removed`,
        r.keptForAdjustments && `${r.keptForAdjustments} kept at zero for their adjustments`,
      ].filter(Boolean);
      const skipped = r.skippedFinalized.length ? ` Finalized, not touched: ${r.skippedFinalized.join(', ')}.` : '';
      const waiting = r.notVerified ? ` ${r.notVerified} entr${r.notVerified === 1 ? 'y is' : 'ies are'} not verified yet and left out.` : '';
      setNotice({ ok: true, text: `${parts.length ? parts.join(', ') : 'Nothing to bill'} for ${monthTitle(month)}.${skipped}${waiting}` });
      void qc.invalidateQueries({ queryKey: ['billing'] });
      void qc.invalidateQueries({ queryKey: ['billing-bill'] });
    },
    onError: (e) => setNotice({ ok: false, text: friendlyBillError(e) }),
  });

  if (data.error?.message === 'billing_migration_pending') {
    return (
      <div className="space-y-4">
        <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          Saved bills need the server database update (migration_2026_10_04_contractor_billing.sql).
          {legacy ? ' Until then this is the live bill — computed from the entries, not saved or locked.' : ''}
        </div>
        {legacy}
      </div>
    );
  }

  const bills = data.data?.bills ?? [];
  const unbilled = data.data?.unbilled ?? [];
  const names = (mode === 'range' ? rangeNames : [
    ...bills.map((b) => ({ key: b.contractorKey, name: b.contractor })),
    ...unbilled.map((u) => ({ key: u.key, name: u.contractor })),
  ]).sort((a, b) => a.name.localeCompare(b.name));
  const shownBills = bills.filter((b) => !only || b.contractorKey === only);
  const shownUnbilled = unbilled.filter((u) => !only || u.key === only);
  const finalized = shownBills.filter((b) => b.status === 'FINALIZED').length;
  const monthOpen = month >= thisMonth();

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="label">View</label>
          <div className="flex rounded-md border border-slate-300 p-0.5">
            {(['month', 'range'] as const).map((m) => (
              <button key={m} type="button" onClick={() => { setMode(m); setNotice(null); }}
                      className={`rounded px-3 py-1.5 text-sm font-medium ${mode === m ? 'bg-brand text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
                {m === 'month' ? 'Month bills' : 'Date range'}
              </button>
            ))}
          </div>
        </div>
        {mode === 'range' && <>
          <div>
            <label className="label">From</label>
            <input type="date" className="input" value={rangeFrom} onChange={(e) => setRangeFrom(e.target.value)} />
          </div>
          <div>
            <label className="label">To</label>
            <input type="date" className="input" value={rangeTo} onChange={(e) => setRangeTo(e.target.value)} />
          </div>
        </>}
        {mode === 'month' && <div>
          <label className="label">Month</label>
          <input
            type="month"
            className="input"
            value={month}
            onChange={(e) => { setMonth(e.target.value); setOnly(''); setNotice(null); }}
          />
        </div>}
        <div>
          <label className="label">Contractor</label>
          <select className="input min-w-[200px]" value={only} onChange={(e) => setOnly(e.target.value)}>
            <option value="">All contractors</option>
            {names.map((k) => <option key={k.key} value={k.key}>{k.name}</option>)}
          </select>
        </div>
        {mode === 'month' && canManage && <button
          className="btn-primary"
          disabled={!validMonth || generate.isPending || data.isLoading}
          onClick={() => { setNotice(null); generate.mutate(); }}
          title="Save the month's live bill as a draft per contractor. Finalized bills are left alone."
        >
          {generate.isPending ? 'Working…' : bills.some((b) => b.status === 'DRAFT') ? 'Regenerate drafts' : 'Generate bills'}
        </button>}
        {mode === 'month' && data.data && (
          <div className="ml-auto text-right">
            <div className="text-[10px] uppercase tracking-wide text-slate-500">
              Saved bills, {monthTitle(month)} · {finalized} of {shownBills.length} finalized
            </div>
            <div className="text-lg font-bold text-brand">{rupees(shownBills.reduce((s, b) => s + b.grandTotal, 0))}</div>
          </div>
        )}
      </div>

      {mode === 'range' && <RangeView from={rangeFrom} to={rangeTo} only={only} onContractors={setRangeNames} />}
      {mode === 'month' && <>
      <p className="text-xs text-slate-500">
        A draft is a snapshot of the entries — regenerate after entries change. Finalizing locks every entry on the bill;
        after that an entry is corrected only with a reason, and money moves only through adjustments.
      </p>

      {!!data.data?.notVerified && (
        <div className="rounded border border-yellow-200 bg-yellow-50 px-3 py-2 text-xs text-yellow-900">
          {data.data.notVerified} production entr{data.data.notVerified === 1 ? 'y' : 'ies'} of {monthTitle(month)} {data.data.notVerified === 1 ? 'is' : 'are'} not
          verified yet and {data.data.notVerified === 1 ? 'is' : 'are'} on no bill. Verify them on Production Entry, then regenerate.
        </div>
      )}

      {notice && (
        <div className={`rounded border px-3 py-2 text-xs ${notice.ok ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-red-200 bg-red-50 text-red-700'}`}>
          {notice.text}
        </div>
      )}

      {data.isLoading && <p className="text-slate-500 text-sm">Loading…</p>}
      {data.isError && <p className="text-red-600 text-sm">Could not load the bills: {friendlyBillError(data.error)}</p>}
      {data.data && shownBills.length === 0 && shownUnbilled.length === 0 && (
        <p className="text-slate-500 text-sm">No production entries in {monthTitle(month)}.</p>
      )}

      {shownBills.map((b) => <BillCard key={b.id} bill={b} monthOpen={monthOpen} canManage={canManage} />)}

      {shownUnbilled.length > 0 && (
        <div className="space-y-3">
          <div className="pt-2">
            <h2 className="text-sm font-semibold text-slate-700">Not billed yet — live from the entries</h2>
            <p className="text-xs text-slate-500">
              {shownUnbilled.length === 1 ? 'This contractor has' : 'These contractors have'} entries in {monthTitle(month)} but no saved bill.
              {canManage ? ` Generate bills to save ${shownUnbilled.length === 1 ? 'it' : 'them'} as drafts.` : ''}
            </p>
          </div>
          {shownUnbilled.map((u) => (
            <div key={u.key} className="card">
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-t-lg border-b border-slate-200 bg-slate-50 px-4 py-2.5">
                <div className="text-sm font-semibold text-slate-700">
                  {u.contractor}
                  <span className="ml-2 rounded bg-slate-200 px-2 py-0.5 text-[11px] font-semibold text-slate-600">Not saved</span>
                </div>
              </div>
              <div className="card-body space-y-2">
                <div className="text-xs text-slate-600">
                  Produced: <b>{inr(u.producedCartons)}</b> cartons · {u.entryCount} entr{u.entryCount === 1 ? 'y' : 'ies'}
                </div>
                <LinesTable lines={u.lines} />
                <div className="flex justify-end gap-3 text-sm">
                  <span className="font-semibold">Total</span>
                  <span className="font-bold text-brand">{rupees(u.total)}</span>
                </div>
                {u.missingRates.length > 0 && <MissingRates names={u.missingRates} />}
              </div>
            </div>
          ))}
        </div>
      )}
      </>}
    </div>
  );
}

interface RangeResponse {
  from: string; to: string;
  contractors: { key: string; contractor: string; lines: BillLineDto[]; total: number; producedCartons: number; missingRates: string[]; entryCount: number }[];
  grandTotal: number;
  notVerified: number;
}

/** Each contractor's work between two dates, live from the verified entries. */
function RangeView({ from, to, only, onContractors }: {
  from: string; to: string; only: string; onContractors: (n: { key: string; name: string }[]) => void;
}) {
  const ok = /^\d{4}-\d{2}-\d{2}$/.test(from) && /^\d{4}-\d{2}-\d{2}$/.test(to) && from <= to;
  const q = useQuery({
    enabled: ok,
    queryKey: ['billing-range', from, to],
    queryFn: () => api<RangeResponse>(`/api/billing/range?from=${from}&to=${to}`),
    retry: (n, e) => n < 2 && !(e instanceof ApiError),
  });
  const list = q.data?.contractors ?? [];
  useEffect(() => { onContractors(list.map((c) => ({ key: c.key, name: c.contractor }))); }, [q.data]); // eslint-disable-line react-hooks/exhaustive-deps
  const shown = list.filter((c) => !only || c.key === only);
  const title = `${dateText(from)} – ${dateText(to)}`;
  if (!ok) return <p className="text-sm text-amber-700">Pick a From date on or before the To date.</p>;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold text-slate-700">Work done {title}</h2>
          <p className="text-xs text-slate-500">Live from the verified entries, at today's rates. Saved bills are by month - switch to Month bills for those.</p>
        </div>
        {q.data && <div className="text-right"><div className="text-[10px] uppercase tracking-wide text-slate-500">Total</div>
          <div className="text-lg font-bold text-brand">{rupees(shown.reduce((s, c) => s + c.total, 0))}</div></div>}
      </div>
      {!!q.data?.notVerified && (
        <div className="rounded border border-yellow-200 bg-yellow-50 px-3 py-2 text-xs text-yellow-900">
          {q.data.notVerified} entr{q.data.notVerified === 1 ? 'y' : 'ies'} in these dates {q.data.notVerified === 1 ? 'is' : 'are'} not verified yet and left out.
        </div>
      )}
      {q.isLoading && <p className="text-slate-500 text-sm">Loading…</p>}
      {q.isError && <p className="text-red-600 text-sm">Could not load: {friendlyBillError(q.error)}</p>}
      {q.data && shown.length === 0 && <p className="text-slate-500 text-sm">No verified production entries in these dates.</p>}
      {shown.map((c) => (
        <div key={c.key} className="card">
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-t-lg border-b border-slate-200 bg-slate-50 px-4 py-2.5">
            <div className="text-sm font-semibold text-slate-700">{c.contractor}</div>
            <div className="text-xs text-slate-500">{title}</div>
          </div>
          <div className="card-body space-y-2">
            <div className="text-xs text-slate-600">
              Produced: <b>{inr(c.producedCartons)}</b> cartons · {c.entryCount} entr{c.entryCount === 1 ? 'y' : 'ies'}
            </div>
            <LinesTable lines={c.lines} />
            <div className="flex justify-end gap-3 text-sm">
              <span className="font-semibold">Total</span>
              <span className="font-bold text-brand">{rupees(c.total)}</span>
            </div>
            {c.missingRates.length > 0 && <MissingRates names={c.missingRates} />}
          </div>
        </div>
      ))}
    </div>
  );
}

function MissingRates({ names }: { names: string[] }) {
  return (
    <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
      No rate set for: {names.join(', ')} — left out of the total, and the bill cannot be finalized until they are set
      on Pasting Rates and the month is regenerated.
    </div>
  );
}

function LinesTable({ lines }: { lines: BillLineDto[] }) {
  return (
    <table className="w-full text-sm">
      <thead className="text-left text-[11px] uppercase text-slate-500 border-b border-slate-200">
        <tr>
          <th className="py-1.5">Box / Work</th>
          <th className="py-1.5 text-right">Qty</th>
          <th className="py-1.5 text-right">Rate</th>
          <th className="py-1.5 text-right">Amount</th>
        </tr>
      </thead>
      <tbody>
        {lines.map((l, i) => (
          <tr key={l.id ?? i} className="border-b border-slate-100">
            <td className="py-1.5">
              {l.operation}
              {l.variant && <span className="ml-1.5 text-xs text-slate-500">— {l.variant}</span>}
              {!l.countsAsProduced && <span className="ml-1.5 text-[10px] text-amber-700">billed only</span>}
              <span className="ml-1.5 text-[10px] text-slate-400">{l.entries} entr{l.entries === 1 ? 'y' : 'ies'}</span>
            </td>
            <td className="py-1.5 text-right">{qtyCell(l.unit, l.qty, l.hours)}</td>
            <td className="py-1.5 text-right text-slate-600">
              {l.rate == null ? <span className="text-red-600">no rate</span> : `${rupees(l.rate)} ${unitLabel(l.unit)}`}
            </td>
            <td className="py-1.5 text-right font-medium">{rupees(l.amount)}</td>
          </tr>
        ))}
        {lines.length === 0 && (
          <tr><td colSpan={4} className="py-3 text-center text-xs text-slate-400">No entries.</td></tr>
        )}
      </tbody>
    </table>
  );
}

function BillCard({ bill, monthOpen, canManage }: { bill: BillDto; monthOpen: boolean; canManage: boolean }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canUnlock = can(user, 'UNLOCK_BILLING');
  const final = bill.status === 'FINALIZED';

  const [err, setErr] = useState('');
  const [showEntries, setShowEntries] = useState(false);
  const [adjOpen, setAdjOpen] = useState(false);
  const [adjSign, setAdjSign] = useState<1 | -1>(-1);
  const [adjAmount, setAdjAmount] = useState('');
  const [adjReason, setAdjReason] = useState('');
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [unlockReason, setUnlockReason] = useState('');

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['billing'] });
    void qc.invalidateQueries({ queryKey: ['billing-bill', bill.id] });
    // Lock state shows on the Entries tab.
    void qc.invalidateQueries({ queryKey: ['production'] });
  };
  const onError = (e: unknown) => setErr(friendlyBillError(e));

  const detail = useQuery({
    enabled: showEntries,
    queryKey: ['billing-bill', bill.id],
    queryFn: () => api<BillDetail>(`/api/billing/${bill.id}`),
  });

  const finalize = useMutation({
    mutationFn: () => api<{ lockedEntries: number }>(`/api/billing/${bill.id}/finalize`, { method: 'POST' }),
    onSuccess: () => { setErr(''); refresh(); },
    onError,
  });
  const unlock = useMutation({
    mutationFn: () => api(`/api/billing/${bill.id}/unlock`, { method: 'POST', body: JSON.stringify({ reason: unlockReason.trim() }) }),
    onSuccess: () => { setErr(''); setUnlockOpen(false); setUnlockReason(''); refresh(); },
    onError,
  });
  const addAdj = useMutation({
    mutationFn: () => api(`/api/billing/${bill.id}/adjustment`, {
      method: 'POST',
      body: JSON.stringify({ amount: adjSign * Number(adjAmount), reason: adjReason.trim() }),
    }),
    onSuccess: () => { setErr(''); setAdjOpen(false); setAdjAmount(''); setAdjReason(''); refresh(); },
    onError,
  });
  const removeAdj = useMutation({
    mutationFn: (lineId: number) => api(`/api/billing/${bill.id}/adjustment/${lineId}`, { method: 'DELETE' }),
    onSuccess: () => { setErr(''); refresh(); },
    onError,
  });

  function doFinalize() {
    const lines = [
      `Finalize ${bill.contractor}'s bill for ${monthTitle(bill.month)} — ${rupees(bill.grandTotal)}?`,
      '',
      `Its ${bill.entryCount} entr${bill.entryCount === 1 ? 'y' : 'ies'} will be locked: after this they can only be corrected by Super Admin / VPPL with a reason, and no new entries can be added for ${bill.contractor} in ${monthTitle(bill.month)}.`,
    ];
    if (monthOpen) lines.push('', `${monthTitle(bill.month)} is not over yet.`);
    if (confirm(lines.join('\n'))) finalize.mutate();
  }

  const adjValid = Number(adjAmount) >= 0.01 && adjReason.trim().length >= 3;
  const unlockValid = unlockReason.trim().length >= 3;

  return (
    <div className="card">
      <div className="card-header flex flex-wrap items-center justify-between gap-2">
        <div>
          <span className="font-semibold">{bill.contractor}</span>
          <span className="ml-2 text-xs font-normal opacity-80">{monthTitle(bill.month)} · Bill #{bill.id}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${final ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
            {final ? '🔒 Finalized' : 'Draft'}
          </span>
          <button className="btn-ghost text-xs py-1" onClick={() => printBill(bill.id)}>Print</button>
          {!final && canManage && (
            <button
              className="btn-ghost text-xs py-1"
              disabled={finalize.isPending || bill.stale || bill.missingRates.length > 0}
              title={bill.stale ? 'Regenerate first — entries changed' : bill.missingRates.length ? 'Set the missing rates first' : 'Lock this bill and its entries'}
              onClick={doFinalize}
            >
              {finalize.isPending ? 'Finalizing…' : 'Finalize'}
            </button>
          )}
          {final && canUnlock && (
            <button className="btn-ghost text-xs py-1" onClick={() => { setUnlockOpen((v) => !v); setErr(''); }}>Unlock</button>
          )}
        </div>
      </div>

      <div className="card-body space-y-3">
        <div className="text-xs text-slate-600 space-y-0.5">
          <div>
            Produced this month: <b>{inr(bill.producedCartons)}</b> cartons
            {' · '}{bill.entryCount} entr{bill.entryCount === 1 ? 'y' : 'ies'}
          </div>
          <div className="text-slate-500">
            Generated {when(bill.generatedOn)}{bill.generatedBy ? ` by ${bill.generatedBy}` : ''}
            {final && bill.finalizedOn && <> · Finalized {when(bill.finalizedOn)}{bill.finalizedBy ? ` by ${bill.finalizedBy}` : ''}</>}
          </div>
          {bill.unlockedOn && (
            <div className="text-slate-500">
              Last unlocked {when(bill.unlockedOn)}{bill.unlockedBy ? ` by ${bill.unlockedBy}` : ''}
              {bill.unlockReason ? ` — “${bill.unlockReason}”` : ''}
            </div>
          )}
        </div>

        {bill.stale && (
          <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            Entries changed since this draft was generated — the entries now come to {rupees(bill.liveTotal)}, this draft
            says {rupees(bill.workTotal)}. Regenerate drafts before finalizing.
          </div>
        )}
        {final && bill.drift !== 0 && (
          <div className="rounded border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-800">
            Entries on this bill were corrected after it was finalized: the work now comes to {rupees(bill.liveTotal)},{' '}
            {rupees(Math.abs(bill.drift))} {bill.drift > 0 ? 'more' : 'less'} than billed. If the difference should be{' '}
            {bill.drift > 0 ? 'paid' : 'recovered'}, add an adjustment of {signedRupees(bill.drift)}.
          </div>
        )}
        {bill.missingRates.length > 0 && <MissingRates names={bill.missingRates} />}

        <LinesTable lines={bill.lines} />

        <table className="w-full text-sm">
          <tbody>
            <tr>
              <td className="py-1 text-right text-slate-600">Work total</td>
              <td className="py-1 w-40 text-right font-medium">{rupees(bill.workTotal)}</td>
            </tr>
            {bill.adjustments.map((a) => (
              <tr key={a.id}>
                <td className="py-1 text-right text-xs text-slate-600">
                  Adjustment: {a.reason}
                  <span className="ml-1.5 text-[10px] text-slate-400">{a.createdBy ?? ''} · {when(a.createdOn)}</span>
                  {!final && (
                    <button
                      className="ml-2 text-[11px] text-red-600 hover:underline"
                      disabled={removeAdj.isPending}
                      onClick={() => { if (confirm(`Remove the adjustment ${signedRupees(a.amount)} (“${a.reason}”)?`)) removeAdj.mutate(a.id); }}
                    >remove</button>
                  )}
                </td>
                <td className={`py-1 w-40 text-right font-medium ${a.amount < 0 ? 'text-red-700' : 'text-emerald-700'}`}>{signedRupees(a.amount)}</td>
              </tr>
            ))}
            <tr className="border-t border-slate-200">
              <td className="py-2 text-right font-semibold">Total payable</td>
              <td className="py-2 w-40 text-right font-bold text-brand">{rupees(bill.grandTotal)}</td>
            </tr>
          </tbody>
        </table>

        {unlockOpen && final && (
          <div className="rounded border border-red-200 bg-red-50 px-3 py-2 space-y-2">
            <p className="text-xs text-red-800">
              Unlocking turns this bill back into a draft and unlocks its {bill.entryCount} entr{bill.entryCount === 1 ? 'y' : 'ies'}.
              The reason is kept on the bill and in the log.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <input
                className="input flex-1 min-w-[220px]"
                placeholder="Reason for unlocking (required)"
                value={unlockReason}
                maxLength={500}
                onChange={(e) => setUnlockReason(e.target.value)}
              />
              <button className="btn-danger text-xs" disabled={!unlockValid || unlock.isPending} onClick={() => unlock.mutate()}>
                {unlock.isPending ? 'Unlocking…' : 'Unlock bill'}
              </button>
              <button className="btn-ghost text-xs" onClick={() => { setUnlockOpen(false); setUnlockReason(''); }}>Cancel</button>
            </div>
          </div>
        )}

        {adjOpen ? (
          <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 space-y-2">
            <p className="text-xs text-slate-600">
              {final
                ? 'This bill is finalized: the adjustment is added to it and logged. It cannot be removed later — correct a wrong one with an opposite adjustment.'
                : 'A deduction (QC rejections through the contractor’s fault, an advance…) or an addition (a missed entry…). Logged with your name and the reason.'}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <select className="input w-auto" value={adjSign} onChange={(e) => setAdjSign(Number(e.target.value) === 1 ? 1 : -1)}>
                <option value={-1}>Deduct (−)</option>
                <option value={1}>Add (+)</option>
              </select>
              <input
                type="number"
                className="input w-36"
                placeholder="Amount ₹"
                min={0.01}
                step={0.01}
                value={adjAmount}
                onChange={(e) => setAdjAmount(e.target.value)}
              />
              <input
                className="input flex-1 min-w-[220px]"
                placeholder="Reason (required)"
                maxLength={500}
                value={adjReason}
                onChange={(e) => setAdjReason(e.target.value)}
              />
              <button className="btn-primary text-xs" disabled={!adjValid || addAdj.isPending} onClick={() => addAdj.mutate()}>
                {addAdj.isPending ? 'Saving…' : 'Save adjustment'}
              </button>
              <button className="btn-ghost text-xs" onClick={() => { setAdjOpen(false); setAdjAmount(''); setAdjReason(''); }}>Cancel</button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-4">
            {canManage && <button className="text-xs text-brand hover:underline" onClick={() => { setAdjOpen(true); setErr(''); }}>
              + Adjustment
            </button>}
            <button className="text-xs text-brand hover:underline" onClick={() => setShowEntries((v) => !v)}>
              {showEntries ? 'Hide' : 'Show'} the {bill.entryCount} entr{bill.entryCount === 1 ? 'y' : 'ies'}
            </button>
          </div>
        )}

        {err && <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{err}</div>}

        {showEntries && (
          <div className="overflow-x-auto border border-slate-200 rounded">
            {detail.isLoading && <p className="px-2 py-2 text-xs text-slate-500">Loading…</p>}
            {detail.isError && <p className="px-2 py-2 text-xs text-red-600">{friendlyBillError(detail.error)}</p>}
            {detail.data && (
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-left text-[10px] uppercase text-slate-500">
                  <tr>
                    <th className="px-2 py-1.5">Date</th>
                    <th className="px-2 py-1.5">Job</th>
                    <th className="px-2 py-1.5">Product</th>
                    <th className="px-2 py-1.5">Box / Work</th>
                    <th className="px-2 py-1.5 text-right">Qty</th>
                    <th className="px-2 py-1.5 text-right">Rate</th>
                    <th className="px-2 py-1.5 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.data.entries.map((e) => (
                    <tr key={e.id} className="border-t border-slate-100">
                      <td className="px-2 py-1 whitespace-nowrap">{e.locked ? '🔒 ' : ''}{dateText(e.date)}</td>
                      <td className="px-2 py-1 font-mono">{e.jobId || '—'}</td>
                      <td className="px-2 py-1 max-w-[240px] truncate" title={e.jobName}>{e.jobName}</td>
                      <td className="px-2 py-1">{entryWork(e)}</td>
                      <td className="px-2 py-1 text-right">{qtyCell(e.unit, e.qty, e.hours)}</td>
                      <td className="px-2 py-1 text-right">{e.rate == null ? '—' : inr(e.rate, 2)}</td>
                      <td className="px-2 py-1 text-right">{rupees(e.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** Print opens the bill on its own page: Print, Download PDF, entries if wanted. */
function printBill(id: number) {
  window.open(`/billing/${id}/print`, '_blank');
}

const billFileName = (bill: BillDetail) =>
  `Pasting bill ${bill.id} ${bill.contractor} ${monthTitle(bill.month)}`.replace(/[\\/:*?"<>|]+/g, ' ').trim();

/**
 * The bill as an A4 sheet - lines, adjustments, total and signatures, then
 * (when asked for) every entry on it.  Styles are inline so the PDF, built in
 * its own frame, comes out the same as the print.
 */
export function buildBillHtml(bill: BillDetail, withEntries: boolean): string {
  const td = 'border:1px solid #000;padding:3px 6px;';
  const th = 'border:1px solid #000;padding:3px 6px;background:#f2f2f2;text-align:left;';
  const final = bill.status === 'FINALIZED';
  const lines = bill.lines.map((l) => `<tr>
      <td style="${td}">${esc(workText(l))}</td>
      <td style="${td}text-align:right">${qtyCell(l.unit, l.qty, l.hours)}</td>
      <td style="${td}text-align:right">${l.rate == null ? 'no rate' : `${inr(l.rate, 2)} ${unitLabel(l.unit)}`}</td>
      <td style="${td}text-align:right">${l.amount == null ? '—' : inr(l.amount, 2)}</td></tr>`).join('');
  const adjustments = bill.adjustments.map((a) => `<tr>
      <td colspan="3" style="${td}text-align:right">Adjustment: ${esc(a.reason)}</td>
      <td style="${td}text-align:right">${a.amount < 0 ? '−' : '+'}${inr(Math.abs(a.amount), 2)}</td></tr>`).join('');
  const entries = bill.entries.map((e) => `<tr style="page-break-inside:avoid">
      <td style="${td}">${dateText(e.date)}</td><td style="${td}">${esc(e.jobId || '—')}</td>
      <td style="${td}">${esc(e.jobName)}</td><td style="${td}">${esc(entryWork(e))}</td>
      <td style="${td}text-align:right">${qtyCell(e.unit, e.qty, e.hours)}</td>
      <td style="${td}text-align:right">${e.amount == null ? '—' : inr(e.amount, 2)}</td></tr>`).join('');
  const status = final
    ? `FINALIZED${bill.finalizedOn ? ` on ${esc(when(bill.finalizedOn))}` : ''}${bill.finalizedBy ? ` by ${esc(bill.finalizedBy)}` : ''}`
    : 'DRAFT — not final, subject to change';

  return `<div style="font-family:Arial,Helvetica,sans-serif;font-size:11px;color:#000">
    <div style="text-align:center;font-weight:bold;font-size:15px">VASUDEV PRINT PACK PRIVATE LIMITED</div>
    <div style="text-align:center;font-weight:bold;font-size:13px;margin:4px 0 2px">PASTING CONTRACTOR BILL — ${esc(monthTitle(bill.month))}</div>
    <div style="text-align:center;margin-bottom:10px;${final ? '' : 'color:#b45309;font-weight:bold'}">Bill #${bill.id} · ${status}</div>
    <div style="margin-bottom:6px"><b>Contractor:</b> ${esc(bill.contractor)}</div>
    <div style="margin-bottom:10px"><b>Produced cartons:</b> ${inr(bill.producedCartons)}</div>
    <table style="border-collapse:collapse;width:100%"><thead><tr><th style="${th}">Box / Work</th><th style="${th}text-align:right">Qty</th><th style="${th}text-align:right">Rate</th><th style="${th}text-align:right">Amount (₹)</th></tr></thead>
    <tbody>${lines}
      <tr><td colspan="3" style="${td}text-align:right">Work total</td><td style="${td}text-align:right">${inr(bill.workTotal, 2)}</td></tr>
      ${adjustments}
      <tr><td colspan="3" style="${td}text-align:right;font-weight:bold">Total payable</td><td style="${td}text-align:right;font-weight:bold">${inr(bill.grandTotal, 2)}</td></tr>
    </tbody></table>
    ${bill.missingRates.length ? `<p style="margin-top:6px">No rate set (not in total): ${esc(bill.missingRates.join(', '))}</p>` : ''}
    <div style="display:flex;justify-content:space-between;margin-top:40px"><span>Contractor's signature</span><span>Authorised Signatory</span></div>
    ${withEntries ? `<div style="margin-top:18px;font-weight:bold">Entries (${bill.entries.length})</div>
    <table style="border-collapse:collapse;width:100%;margin-top:4px"><thead><tr><th style="${th}">Date</th><th style="${th}">Job</th><th style="${th}">Product</th><th style="${th}">Box / Work</th><th style="${th}text-align:right">Qty</th><th style="${th}text-align:right">Amount (₹)</th></tr></thead>
    <tbody>${entries}</tbody></table>` : ''}
  </div>`;
}

const sheetBtn = { padding: '6px 16px', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13 } as const;

/**
 * /billing/:id/print - the bill on an A4 sheet with Print and Download PDF.
 * Just the bill by default; "Include every entry" adds the entry list.
 */
export function BillPrintPage() {
  const { id = '' } = useParams();
  const q = useQuery({ queryKey: ['bill-print', id], queryFn: () => api<BillDetail>(`/api/billing/${id}`) });
  const [withEntries, setWithEntries] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bill = q.data;

  // The browser's "Save as PDF" names the file after the page title.
  useEffect(() => {
    if (!bill) return;
    const before = document.title;
    document.title = billFileName(bill);
    return () => { document.title = before; };
  }, [bill]);

  async function downloadPdf() {
    if (!bill) return;
    setBusy(true);
    setError(null);
    let iframe: HTMLIFrameElement | null = null;
    let probeFix: HTMLStyleElement | null = null;
    try {
      const [{ default: html2pdf }, { BASELINE_PROBE_FIX }] = await Promise.all([import('html2pdf.js'), import('../lib/generateBoardPoPdf')]);
      probeFix = document.createElement('style');
      probeFix.textContent = BASELINE_PROBE_FIX;
      document.head.appendChild(probeFix);
      // Rendered in a hidden frame (an off-screen div measures at zero height).
      iframe = document.createElement('iframe');
      iframe.style.cssText = 'position:fixed;left:-9999px;top:-9999px;width:190mm;height:277mm;visibility:hidden;';
      document.body.appendChild(iframe);
      const page = iframe.contentDocument!;
      page.open();
      page.write(`<!doctype html><html><head><meta charset="utf-8"></head><body style="margin:0;background:#fff">${buildBillHtml(bill, withEntries)}</body></html>`);
      page.close();
      // Tall enough for every page of entries.
      iframe.style.height = `${page.body.scrollHeight + 50}px`;
      const opts = {
        margin: 10,
        filename: `${billFileName(bill)}.pdf`,
        image: { type: 'jpeg' as const, quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: '#ffffff' },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' as const },
        pagebreak: { mode: ['css', 'legacy'], avoid: 'tr' },
      };
      await html2pdf().set(opts).from(page.body).save();
    } catch (e) {
      setError(`PDF failed: ${(e as Error).message}`);
    } finally {
      iframe?.remove();
      probeFix?.remove();
      setBusy(false);
    }
  }

  return (
    <>
      <style>{`
        @page { size: A4; margin: 10mm; }
        @media print {
          .no-print { display: none !important; }
          .bill-desk { background: none !important; padding: 0 !important; min-height: 0 !important; }
          .bill-sheet { width: auto !important; padding: 0 !important; margin: 0 !important; box-shadow: none !important; }
        }
      `}</style>
      <div className="no-print" style={{ padding: '12px 24px', display: 'flex', gap: 8, alignItems: 'center', borderBottom: '1px solid #ddd', flexWrap: 'wrap' }}>
        <button onClick={() => window.print()} disabled={!bill} style={{ ...sheetBtn, background: '#0f4c75', color: '#fff' }}>Print</button>
        <button onClick={() => void downloadPdf()} disabled={!bill || busy} style={{ ...sheetBtn, background: '#0f4c75', color: '#fff', opacity: busy ? 0.6 : 1 }}>
          {busy ? 'Preparing PDF…' : 'Download PDF'}
        </button>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, marginLeft: 8 }}>
          <input type="checkbox" checked={withEntries} onChange={(e) => setWithEntries(e.target.checked)} />
          Include every entry{bill ? ` (${bill.entries.length})` : ''}
        </label>
        <button onClick={() => window.close()} style={{ ...sheetBtn, background: '#e2e8f0' }}>Close</button>
        {error && <span style={{ color: '#b91c1c', fontSize: 13 }}>{error}</span>}
      </div>
      <div className="bill-desk" style={{ background: '#e2e8f0', minHeight: '100vh', padding: '16px 0', overflowX: 'auto' }}>
        {q.isLoading && <p style={{ textAlign: 'center', fontFamily: 'Arial' }}>Loading the bill…</p>}
        {q.isError && <p style={{ textAlign: 'center', fontFamily: 'Arial', color: '#b91c1c' }}>Could not load the bill: {friendlyBillError(q.error)}</p>}
        {bill && (
          <div
            className="bill-sheet"
            style={{ width: '210mm', margin: '0 auto', background: '#fff', padding: '10mm', boxShadow: '0 1px 4px rgba(0,0,0,.15)', boxSizing: 'border-box' }}
            dangerouslySetInnerHTML={{ __html: buildBillHtml(bill, withEntries) }}
          />
        )}
      </div>
    </>
  );
}
